//
// Copyright 2025 DXOS.org
//

// @import-as-namespace

import * as Context from 'effect/Context';
import type * as Effect$ from 'effect/Effect';
import type * as Layer$ from 'effect/Layer';
import * as Atom from 'effect/reactivity/Atom';
import * as Schema$ from 'effect/Schema';

import type { OpaqueToolkit, AiModelResolver as AiModelResolver$ } from '@dxos/ai';
import * as Capability$ from '@dxos/app-framework/Capability';
import * as AppGraphBuilder$ from '@dxos/app-graph/AppGraphBuilder';
import type * as AppGraphNode$ from '@dxos/app-graph/AppGraphNode';
import type { Client } from '@dxos/client';
import type { Space } from '@dxos/client/echo';
import * as Credential from '@dxos/compute/Credential';
import * as Operation from '@dxos/compute/Operation';
import * as Skill from '@dxos/compute/Skill';
import type { Database, Obj, Type } from '@dxos/echo';
import type * as Retention from '@dxos/graph/Retention';
import { type Translator as Translator$ } from '@dxos/i18n';
import { type URI } from '@dxos/keys';
import { Progress } from '@dxos/progress';
import type { AnchoredTo } from '@dxos/types';
import * as Position from '@dxos/util/Position';

// eslint-disable-next-line @dxos/rules/import-as-namespace
import type * as AppUpdate$ from '../app/AppUpdate.ts';
// eslint-disable-next-line @dxos/rules/import-as-namespace
import type * as Translations$ from '../app/Translations.ts';
import type * as AppSettings from '../types/AppSettings.ts';
// eslint-disable-next-line @dxos/rules/import-as-namespace
import type * as ObservabilityMapping$ from './ObservabilityMapping.ts';
import type * as TourModule from './Tour.ts';

export const LAYOUT_CAPABILITY_ID = 'org.dxos.app-framework.capability.layout';

// TODO(burdon): See Accept attribute (uses MIME types).
// E.g., 'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
export const defaultFileTypes = {
  images: ['png', 'jpg', 'jpeg', 'gif'],
  media: ['mp3', 'mp4', 'mov', 'avi'],
  text: ['pdf', 'txt', 'md'],
};

export const FileInfoSchema = Schema$.Struct({
  name: Schema$.String,
  type: Schema$.String,
  url: Schema$.optional(Schema$.String),
  cid: Schema$.optional(Schema$.String),
});

export type FileInfo = Schema$.Schema.Type<typeof FileInfoSchema>;

export type Layout = Readonly<{
  mode: string;
  dialogOpen: boolean;
  sidebarOpen: boolean;
  complementarySidebarOpen: boolean;
  /**
   * The id of the active workspace, where a workspace is a set of active items.
   */
  workspace: string;
  /**
   * Identifiers of items which are currently active in the application.
   */
  active: string[];
  /**
   * Identifiers of items which were previously active in the application.
   */
  inactive: string[];
  /**
   * Identifier of the item which should be scrolled into view.
   */
  scrollIntoView: string | undefined;
}>;

/**
 * Layout capability - provides reactive access to the current layout state.
 * @category Capability
 */
export const Layout = Capability$.makeSingleton<Atom.Atom<Layout>>()(LAYOUT_CAPABILITY_ID);

/**
 * @category Capability
 */
export const Translations = Capability$.make<Readonly<Translations$.Resource[]>>()(
  'org.dxos.app-framework.capability.translations',
);

/**
 * Provides access to the shared, framework-agnostic translation instance so non-React code
 * (operations, services, Effect programs) can translate strings dynamically.
 * @category Capability
 */
export const Translator = Capability$.makeSingleton<Translator$>()('org.dxos.app-framework.capability.translator');

/**
 * Effect service for the {@link Translator} capability, consumable via `yield* TranslatorService`
 * once {@link translatorLayer} is provided.
 */
export class TranslatorService extends Context.Service<TranslatorService, Translator$>()(
  '@dxos/app-toolkit/TranslatorService',
) {}

/**
 * Layer that resolves {@link TranslatorService} from the {@link Translator} capability.
 */
export const translatorLayer: Layer$.Layer<TranslatorService, never, Capability$.Service> = Capability$.asLayer(
  Translator,
  TranslatorService,
);

/** A writer scoped to a single plugin's compartment — the only way to mutate the stats store. */
export type StatsCompartment = Readonly<{
  /** Replace this compartment's value. */
  set: (stats: unknown) => void;
  /** Remove this compartment. */
  clear: () => void;
}>;

/**
 * A generic, reactive store for stats/telemetry, partitioned into one compartment per plugin. Every
 * consumer can READ the entire store (all compartments) reactively, but can only WRITE its own
 * compartment — obtained via `compartment(pluginKey)` with the plugin's own key (`meta.profile.key`).
 * Currently in-memory (discarded on reload); persistence may be layered on later, so the name is not
 * tied to transience. Contributed by a host plugin (plugin-debug) — writers resolve it with
 * `Capability.getAll` so writing is a no-op when no host is loaded (e.g. production without devtools).
 * @category Capability
 */
export type StatsPanelStore = Readonly<{
  /** Reactive map of all compartments keyed by plugin key (full read access). */
  statsAtom: Atom.Writable<Record<string, unknown>>;
  /** Read one plugin's compartment. */
  get: (pluginKey: string) => unknown;
  /** A writer scoped to one plugin's compartment — the only write path. */
  compartment: (pluginKey: string) => StatsCompartment;
}>;

/**
 * @category Capability
 */
export const StatsPanel = Capability$.makeSingleton<StatsPanelStore>()('org.dxos.app-toolkit.capability.statsPanel');

/**
 * The graph builder instance. Its `graph` is the reactive node tree; the builder also carries the
 * `urlKey` declarations and node→extension provenance that URL resolution (`@dxos/app-graph`'s
 * `path-resolution.ts`) reads and reverse-maps — neither derivable from `graph` alone.
 */
export type AppGraph = AppGraphBuilder$.GraphBuilder;

/**
 * @category Capability
 */
export const AppGraph = Capability$.makeSingleton<AppGraph>()('org.dxos.app-framework.capability.appGraph');

/**
 * @category Capability
 */
export const AppGraphBuilder = Capability$.make<AppGraphBuilder$.BuilderExtensions>()(
  'org.dxos.app-framework.capability.appGraphBuilder',
);

/** Nodes the graph must keep loaded, contributed by each plugin that knows what it is showing. */
export type AppGraphRetention = Retention.Retention<AppGraphNode$.RelationInput>;

/**
 * @category Capability
 */
export const AppGraphRetention = Capability$.make<AppGraphRetention>()(
  'org.dxos.app-framework.capability.appGraphRetention',
);

export type Settings = {
  prefix: string;
  // Settings are persisted as plain atoms, so the schema is always context-free
  // (`R = never`); this lets a schema-driven form decode/validate it directly.
  schema: Schema$.Codec<any, any, never, never>;
  atom: Atom.Writable<any>;
};

/**
 * Type guard to check if a value is a Settings object.
 */
export const isSettings = (value: unknown): value is Settings =>
  typeof value === 'object' &&
  value !== null &&
  'prefix' in value &&
  typeof (value as Settings).prefix === 'string' &&
  'atom' in value &&
  Atom.isAtom(value.atom) &&
  Atom.isWritable(value.atom);

/**
 * @category Capability
 */
export const Settings = Capability$.make<Settings>()('org.dxos.app-framework.capability.settings');

/**
 * Control surface over the device-synced settings store, which projects every {@link Settings}
 * contribution (plus the plugin set) into the settings space so they follow the identity across
 * devices, with per-key device overrides.
 *
 * Contributed once the settings space is open, so consumers must tolerate its absence.
 */
export type SettingsSync = {
  /** Settings prefixes this device writes locally rather than sharing. */
  readonly unsynced: Atom.Atom<readonly string[]>;
  /** Take a prefix off the account for this device. Lossless, and no other device is touched. */
  takeLocal(prefix: string): void;
  /**
   * Hand a prefix back to the account, keeping the side named by `adopt` wherever
   * {@link conflicts} reports a disagreement. The only direction that discards anything.
   */
  rejoinAccount(prefix: string, options?: { adopt?: AppSettings.Adopt }): void;
  /** Keys that rejoining the account would change. Read on demand rather than reactively. */
  conflicts(prefix: string): readonly string[];
  /** Which settings this device keeps to itself, by prefix. */
  readonly pinned: Atom.Atom<AppSettings.DeviceSettings>;
  /** Keep one key on this device — the per-key counterpart of {@link takeLocal}. */
  pinKey(prefix: string, key: string): void;
  /** Hand one key back to the account — the per-key counterpart of {@link rejoinAccount}. */
  unpinKey(prefix: string, key: string): void;
};

/**
 * @category Capability
 */
export const SettingsSync = Capability$.makeSingleton<SettingsSync>()('org.dxos.app-framework.capability.settingsSync');

export type Schema = ReadonlyArray<Type.AnyEntity>;

/**
 * @category Capability
 */
export const Schema = Capability$.make<Schema>()('org.dxos.app-framework.capability.schema');

export type Toolkit = OpaqueToolkit.OpaqueToolkit;

/**
 * @category Capability
 */
export const Toolkit = Capability$.make<Toolkit>()('org.dxos.app-framework.capability.aiToolkit');

/**
 * @category Capability
 */
export const SkillDefinition = Capability$.make<Skill.Definition>()(
  'org.dxos.app-framework.capability.skillDefinition',
);

/**
 * A static asset bundled with a plugin's published package, exposed for
 * other plugins to read.
 *
 * Contributors import the raw file (e.g. `import spec from '../PLUGIN.mdl?raw'`)
 * and contribute it via this capability from a dependency-mode module. Consumers
 * read all contributions with `Capability.getAll(AppCapabilities.PluginAsset)`.
 */
export type PluginAsset = Readonly<{
  /** Owning plugin id (matches `Plugin.Meta.id`). */
  pluginId: string;
  /** Path within the plugin package — typically equal to `Plugin.Meta.spec`. */
  path: string;
  /** Raw text content. */
  content: string;
  /** Optional MIME type (e.g. `text/markdown`, `application/x-mdl`). */
  mimeType?: string;
}>;

/**
 * @category Capability
 */
export const PluginAsset = Capability$.make<PluginAsset>()('org.dxos.app-framework.capability.pluginAsset');

/**
 * A starting point a plugin offers for a new space: the defaults the create dialog pre-fills, plus
 * the content to write once the space exists.
 *
 * Build one with `SampleSpace.makeTemplate`.
 */
export type SpaceTemplate = Readonly<{
  /** Stable id, namespaced by the owning plugin; the value the create form carries. */
  id: string;
  /** Name for the picker, and the default space name when the template is chosen. */
  label: string;
  /** One line on what the template creates. */
  description?: string;
  /** An `iconValues` name, as space properties carry. */
  icon?: string;
  hue?: string;
  /** Omit from the create picker; reachable only by id. */
  hidden?: boolean;
  /** Registers the content's types on the client, then writes it into the new space. */
  apply: (options: { readonly client: Client; readonly space: Space }) => Promise<void>;
}>;

/**
 * @category Capability
 */
export const SpaceTemplate = Capability$.make<SpaceTemplate>()('org.dxos.app-framework.capability.spaceTemplate');

/**
 * Plugins can contribute model resolvers. The `Credential.CredentialsService` requirement is
 * supplied by the active-space resolver — BYOK-aware resolvers wrap their HTTP client with
 * `Header.byokLayer(...)`; the rest carry it through unused.
 */
export const AiModelResolver = Capability$.make<
  Layer$.Layer<AiModelResolver$.AiModelResolver, never, Credential.CredentialsService>
>()('org.dxos.app-framework.capability.aiModelResolver');

export type FileUploader = (db: Database.Database, file: File) => Promise<FileInfo | undefined>;

/**
 * @category Capability
 */
export const FileUploader = Capability$.make<FileUploader>()('org.dxos.app-framework.capability.fileUploader');

export type AnchorSort = {
  key: string;
  sort: (anchorA: AnchoredTo.AnchoredTo, anchorB: AnchoredTo.AnchoredTo) => number;
};

/**
 * @category Capability
 */
export const AnchorSort = Capability$.make<AnchorSort>()('org.dxos.app-framework.capability.anchorSort');

/** Anchor→text resolution contributed per typename by plugins whose objects support cursor-range anchors. */
export type AnchorResolver = Readonly<{
  key: string;
  /** Resolve an anchor (`"${fromCursor}:${toCursor}"`) to the text it spans, or `undefined` when unresolvable. */
  getText: (obj: any, anchor: string) => string | undefined;
}>;

/**
 * @category Capability
 */
export const AnchorResolver = Capability$.make<AnchorResolver>()('org.dxos.app-framework.capability.anchorResolver');

/** Text content extractor contributed per typename by plugins that support text extraction. */
export type TextContent = Readonly<{
  id: string;
  getTextContent: (object: any) => Promise<string | undefined>;
}>;

/**
 * @category Capability
 */
export const TextContent = Capability$.make<TextContent>()('org.dxos.app-framework.capability.textContent');

/** Comment configuration contributed per typename by plugins that support commenting. */
export type CommentConfig = Readonly<{
  id: string;
  comments: 'anchored' | 'unanchored';
  selectionMode?: string;
  scrollToAnchor?: Operation.Definition.Any;
}>;

/**
 * @category Capability
 */
export const CommentConfig = Capability$.make<CommentConfig>()('org.dxos.app-framework.capability.commentConfig');

/**
 * Where an object of a tagged type goes when it is created without a target. Keyed by a tag on the
 * type's `Annotation.UserType`; see `DefaultParent.resolve`, which asks the matching rules in `position`
 * order and takes the first parent one returns.
 * @category Capability
 */
export type DefaultParent = {
  /** The `Annotation.UserType` tag this rule applies to. */
  readonly tag: string;
  /** The parent for the object; undefined passes to the next rule, as does a failure (which is logged). */
  readonly resolve: (object: Obj.Unknown) => Effect$.Effect<Obj.Unknown | undefined, Error, Database.Service>;
  readonly position?: Position.Position;
};

export const DefaultParent = Capability$.make<DefaultParent>()('org.dxos.app-framework.capability.defaultParent');

export type NavigationTarget = {
  /** Navigation path usable with the Open operation. */
  path: string;
  /** Human-readable label. */
  label: string;
  /** Object type. */
  type: string;
  /**
   * Sort order among the targets resolved for one query. A resolver that only produces a generic path —
   * the database subtree, which guarantees every ECHO object *a* path but is not where the tree shows
   * it — declares `Position.last`, so a resolver that knows the object's canonical home (its collection,
   * a type section) is preferred. Only the resolver knows how specific its answer is, hence a declared
   * position rather than path sniffing by the caller.
   */
  position?: Position.Position;
};

export type NavigationQuery = {
  uri?: URI.URI;
};

/**
 * Resolves a query to navigation targets.
 * Each plugin interprets the query and returns matching targets.
 * When called without a query, returns the plugin's default navigable pages.
 *
 * Requires `Database.Service`: a resolver turns an object URI into a path, which means loading the
 * object. The caller derives the database from the query's space and provides the layer — see
 * `NavigationOperation.ResolveNavigationTargets`, the one entry point that runs every resolver.
 * @category Capability
 */
export type NavigationTargetResolver = (
  query?: NavigationQuery,
) => Effect$.Effect<NavigationTarget[], never, Database.Service>;

export const NavigationTargetResolver = Capability$.make<NavigationTargetResolver>()(
  'org.dxos.app-framework.capability.navigationTargetResolver',
);

/**
 * What a {@link NavigationTargetLoader} was able to determine about a target.
 *
 * Three-valued because the caller's responses to a missing node are opposites: it waits for one that
 * is merely late and fails fast on one that is absent. `absent` is therefore reserved for a store
 * that actually answered — an unreachable edge or an unparseable id is `unknown`.
 * @category Capability
 */
export type NavigationTargetVerdict = 'exists' | 'absent' | 'unknown';

/**
 * Loads/verifies a navigation target so graph resolution can materialize its node. Contributed by the
 * plugin that owns object storage (plugin-client), consumed by layout plugins — this is the
 * abstraction that keeps layout plugins from depending on the client for loading. `load` loads the
 * object into local ECHO when present locally (so a URL-driven restore materializes the plank's
 * node), and resolves `exists` if the object is present locally or, as a fallback, remotely. A
 * remote-only object resolves `exists` but cannot render until it replicates locally.
 *
 * Omitting `entityId` asks about the space itself.
 * @category Capability
 */
export type NavigationTargetLoader = Readonly<{
  id: string;
  load: (target: { spaceId: string; entityId?: string }) => Effect$.Effect<NavigationTargetVerdict>;
}>;

export const NavigationTargetLoader = Capability$.make<NavigationTargetLoader>()(
  'org.dxos.app-framework.capability.navigationTargetLoader',
);

/**
 * Handler called by layout plugins on navigation events (page load, popstate, deep link).
 * Plugins contribute handlers to react to URL query params or other URL parts
 * without the layout plugin needing to know about specific params.
 * @category Capability
 */
export type NavigationHandler = (url: URL) => Effect$.Effect<void>;

export const NavigationHandler = Capability$.make<NavigationHandler>()(
  'org.dxos.app-toolkit.capability.navigationHandler',
);

/** A transient progress monitor handle — the update side of one registry entry. */
export type ProgressMonitor = Progress.TaskHandle;

/**
 * A registry of live progress providers, exposed as reactive atoms. Producers `register` a monitor
 * (keyed by a stable `name`, with a display `label`), advance/complete it, and `remove()` it when
 * done. Consumers read the aggregate `snapshotAtom` (e.g. the R0 rail popover) or a single provider's
 * `monitorAtom(name)` (e.g. an article's inline meter). Contributed by an always-loaded host
 * (`plugin-progress`); backed by the shared `@dxos/progress` core.
 */
export type ProgressRegistry = Readonly<{
  /** Aggregate snapshot of all active providers. */
  snapshotAtom: Atom.Atom<Progress.Snapshot>;
  /** One provider's reactive state, by name (stable/memoized per name). */
  monitorAtom: (name: string) => Atom.Atom<Progress.TaskProgress | undefined>;
  /**
   * Register (or resume) a provider and mark it running. Pass `onCancel` to make it cancellable — the
   * meter then shows a cancel control that invokes {@link ProgressRegistry.cancel}.
   */
  register: (name: string, options?: { label?: string; total?: number; onCancel?: () => void }) => ProgressMonitor;
  /** Invoke a provider's registered `onCancel` handler (no-op if it is not cancellable). */
  cancel: (name: string) => void;
  /** Non-reactive read of the current snapshot. */
  snapshot: () => Progress.Snapshot;
}>;

/**
 * @category Capability
 */
export const ProgressRegistry = Capability$.makeSingleton<ProgressRegistry>()(
  'org.dxos.app-toolkit.capability.progressRegistry',
);

/**
 * The app's update channel, contributed by whichever plugin owns updates on this platform —
 * `plugin-native` for the desktop OTA updater, `plugin-pwa` for the service worker. Exactly one
 * contributes at a time, so a settings surface can render either without knowing which it got.
 * @category Capability
 */
export const UpdateManager = Capability$.makeSingleton<AppUpdate$.Manager>()(
  'org.dxos.app-toolkit.capability.updateManager',
);

export type ObservabilityMapping = ObservabilityMapping$.ObservabilityMapping;

/**
 * Observability event registration — contributed by the plugin that owns the operation, consumed by
 * a listener over the invocation stream so the operation itself stays free of telemetry.
 * @category Capability
 */
export const ObservabilityMapping = Capability$.make<ObservabilityMapping[]>()(
  'org.dxos.app-toolkit.capability.observabilityMapping',
);

export type Tour = TourModule.Definition;

/**
 * A guided tour.
 * @category Capability
 */
export const Tour = Capability$.make<Tour>()('org.dxos.app-toolkit.capability.tour');

export type TourFragment = TourModule.Fragment;

/**
 * Steps contributed into whichever tour is running, by the plugin that owns the feature they explain.
 * @category Capability
 */
export const TourFragment = Capability$.make<TourFragment>()('org.dxos.app-toolkit.capability.tourFragment');
